import React, { useState, useMemo } from "react";
import {
  X,
  CalendarDays,
  ImagePlus,
  MapPin,
  Ticket,
  PlusCircle,
  Trash2,
  Users,
  BadgeDollarSign,
  FileText,
  LinkIcon,
} from "lucide-react";

export default function EditEventModal({ event, onClose, onSave }) {
  const initialTicketTiers = useMemo(() => {
    if (Array.isArray(event.ticketTiers) && event.ticketTiers.length > 0) {
      return event.ticketTiers.map((tier) => ({
        title: tier.title || "",
        price: tier.price ?? "",
        quantity: tier.quantity ?? "",
      }));
    }

    return [
      {
        title: "General Admission",
        price: event.price ?? "",
        quantity: event.numTickets ?? "",
      },
    ];
  }, [event]);

  const [name, setName] = useState(event.name || "");
  const [description, setDescription] = useState(event.description || "");
  const [location, setLocation] = useState(event.location || "");
  const [dateTime, setDateTime] = useState(
    event.dateTime ? new Date(event.dateTime).toISOString().slice(0, 16) : ""
  );
  const [ticketTiers, setTicketTiers] = useState(initialTicketTiers);
  const [imagePreview, setImagePreview] = useState(event.image || "");
  const [newImageFile, setNewImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState(event.admins?.join(", ") || "");
  const [customUrl, setCustomUrl] = useState(event.customUrl || "");

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const updateTier = (index, field, value) => {
    setTicketTiers((prev) =>
      prev.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier))
    );
  };

  const formatCustomUrl = (value) => {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

  const addTier = () => {
    setTicketTiers((prev) => [
      ...prev,
      { title: "", price: "", quantity: "" },
    ]);
  };

  const removeTier = (index) => {
    setTicketTiers((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const cleanedTiers = ticketTiers
        .map((tier) => ({
          title: String(tier.title || "").trim(),
          price: Number(tier.price),
          quantity: Number(tier.quantity),
        }))
        .filter(
          (tier) =>
            tier.title &&
            !Number.isNaN(tier.price) &&
            !Number.isNaN(tier.quantity) &&
            tier.price >= 0 &&
            tier.quantity > 0
        );

      if (!cleanedTiers.length) {
        throw new Error("Please include at least one valid ticket tier.");
      }

      const updatedEventPayload = {
        name,
        description,
        customUrl,
        location,
        dateTime: new Date(dateTime).toISOString(),
        admins: admins
          .split(",")
          .map((a) => a.trim())
          .filter((a) => a),
        ticketTiers: JSON.stringify(cleanedTiers),
      };

      const res = await fetch(`/api/updateEvent/${event._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedEventPayload),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to update event details.");
      }

      let finalUpdatedEvent = data.event;

      if (newImageFile) {
        const formData = new FormData();
        formData.append("image", newImageFile);

        const imgRes = await fetch(`/api/updateEventImage/${event._id}`, {
          method: "PUT",
          body: formData,
        });

        const imgData = await imgRes.json();
        if (!imgRes.ok || !imgData.success) {
          throw new Error(imgData.message || "Failed to update event image.");
        }

        finalUpdatedEvent = {
          ...finalUpdatedEvent,
          image: imgData.event.image,
        };
      }

      onSave(finalUpdatedEvent);
      onClose();
    } catch (err) {
      console.error(err);
      alert(err.message || "Error updating event.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5 sm:px-8">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
              Edit Event
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              Update event details, ticket tiers, admins, and event artwork.
            </p>
          </div>

          <button
            onClick={onClose}
            className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close edit modal"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="max-h-[calc(90vh-88px)] overflow-y-auto px-6 py-6 sm:px-8"
        >
          <div className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Event Name
              </label>
              <div className="relative">
                <PlusCircle className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                  placeholder="Enter event name"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Event Description
              </label>
              <div className="relative">
                <FileText className="pointer-events-none absolute left-4 top-4 h-4 w-4 text-slate-400" />
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="min-h-[140px] w-full resize-y rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                  placeholder="Describe the event, lineup, schedule, venue details, or anything guests should know."
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                This description will show on the public event details page.
              </p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Custom Event URL
              </label>
              <div className="relative">
                <LinkIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={customUrl}
                  onChange={(e) => setCustomUrl(formatCustomUrl(e.target.value))}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                  placeholder="summer-kickoff"
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Your event page will be available at /events/{customUrl || "custom-url"}.
                Leave blank to use the default event ID link.
              </p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Location
              </label>
              <div className="relative">
                <MapPin className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                  placeholder="Enter event location"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Date and Time
              </label>
              <div className="relative">
                <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="datetime-local"
                  value={dateTime}
                  onChange={(e) => setDateTime(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-6">
              <div className="mb-4 flex items-center justify-between gap-4">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    Ticket Tiers
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Update ticket names, prices, and inventory for each tier.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={addTier}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  <PlusCircle className="h-4 w-4" />
                  Add Tier
                </button>
              </div>

              <div className="space-y-4">
                {ticketTiers.map((tier, index) => (
                  <div
                    key={index}
                    className="rounded-3xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-900">
                        Tier {index + 1}
                      </p>

                      {ticketTiers.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTier(index)}
                          className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm font-medium text-red-600 ring-1 ring-slate-200 transition hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                          Tier Title
                        </label>
                        <div className="relative">
                          <Ticket className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <input
                            type="text"
                            value={tier.title}
                            onChange={(e) =>
                              updateTier(index, "title", e.target.value)
                            }
                            className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                            placeholder="General Admission"
                          />
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">
                            Price
                          </label>
                          <div className="relative">
                            <BadgeDollarSign className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={tier.price}
                              onChange={(e) =>
                                updateTier(index, "price", e.target.value)
                              }
                              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                              placeholder="25.00"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-medium text-slate-700">
                            Quantity
                          </label>
                          <div className="relative">
                            <Ticket className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                              type="number"
                              min="1"
                              value={tier.quantity}
                              onChange={(e) =>
                                updateTier(index, "quantity", e.target.value)
                              }
                              className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                              placeholder="100"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Admins
              </label>
              <div className="relative">
                <Users className="pointer-events-none absolute left-4 top-4 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  value={admins}
                  onChange={(e) => setAdmins(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                  placeholder="admin1@example.com, admin2@example.com"
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Separate multiple admin emails with commas.
              </p>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Event Image
              </label>
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="mb-4 h-48 w-full rounded-2xl object-cover"
                  />
                ) : (
                  <div className="mb-4 flex h-48 w-full items-center justify-center rounded-2xl bg-slate-100 text-sm text-slate-500">
                    No image uploaded yet
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <ImagePlus className="h-4 w-4 text-slate-400" />
                  Upload a new event image
                </div>

                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="mt-3 block w-full text-sm text-slate-600 file:mr-4 file:rounded-full file:border-0 file:bg-slate-900 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-slate-800"
                />
              </div>
            </div>
          </div>

          <div className="mt-8 flex flex-col-reverse gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-2xl bg-slate-100 px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-200"
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className={`rounded-2xl px-5 py-3 text-sm font-semibold text-white transition ${
                loading
                  ? "cursor-not-allowed bg-slate-400"
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}